/**
 * WebSocket Service for React client
 * Handles real-time communication with the server
 */

/*
 * Connecting, connected, acknowledged, disconnected and every reconnection
 * attempt used to be logged unconditionally. That is five lines in the console
 * on an ordinary page load, and in development React's StrictMode runs effects
 * twice, so it also produced a permanent "already connected or connecting"
 * that reads like a bug and is not one.
 *
 * The routine lifecycle is useful when the socket is the thing being debugged
 * and noise the rest of the time, so it is behind a flag. Warnings and errors
 * are untouched: those report something actually wrong.
 *
 * Turn it on with REACT_APP_DEBUG_WS=true, or from the console at any time with
 * localStorage.setItem('debugWebSocket', 'true').
 */
const wsDebug = (...args) => {
  let opted = false;
  try {
    opted = window.localStorage.getItem('debugWebSocket') === 'true';
  } catch {
    // Private browsing and blocked storage both throw. Not a reason to fail.
  }
  if (process.env.REACT_APP_DEBUG_WS === 'true' || opted) console.log(...args);
};

// WebSocket close codes
const WS_CLOSE_CODES = {
  NORMAL_CLOSURE: 1000,
  AUTH_REQUIRED: 4001,
  INVALID_TOKEN: 4003
};

class WebSocketService {
  constructor() {
    this.ws = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.listeners = new Map();
    this.isConnected = false;
    this.intentionalDisconnect = false;
  }

  /**
   * Connect to WebSocket server
   * @param {string} token - JWT authentication token
   */
  connect(token) {
    if (this.ws && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)) {
      wsDebug('WebSocket already connected or connecting');
      return;
    }

    if (!token) {
      console.warn('Cannot connect WebSocket: no token provided');
      return;
    }

    // Reset intentional disconnect flag
    this.intentionalDisconnect = false;

    // Determine WebSocket URL
    // If REACT_APP_API_URL is set, derive WebSocket URL from it
    // Otherwise, use the current window location
    let wsUrl;
    const apiUrl = process.env.REACT_APP_API_URL;
    
    if (apiUrl && /^https?:\/\//.test(apiUrl)) {
      // Parse the API URL and convert to WebSocket URL
      const url = new URL(apiUrl);
      const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${wsProtocol}//${url.host}/ws?token=${encodeURIComponent(token)}`;
    } else {
      // Use current window location
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = process.env.REACT_APP_WS_URL || window.location.host;
      wsUrl = `${protocol}//${host}/ws?token=${encodeURIComponent(token)}`;
    }

    wsDebug('Connecting to WebSocket:', wsUrl.replace(/token=[^&]+/, 'token=***'));

    try {
      this.ws = new WebSocket(wsUrl);
      
      this.ws.onopen = () => {
        wsDebug('WebSocket connected');
        this.isConnected = true;
        this.reconnectAttempts = 0;
        this.emit('connected', {});
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (err) {
          console.error('Failed to parse WebSocket message:', err);
        }
      };

      this.ws.onclose = (event) => {
        wsDebug('WebSocket disconnected', event.code, event.reason);
        this.isConnected = false;
        this.emit('disconnected', { code: event.code, reason: event.reason });
        
        // Attempt reconnection if not intentionally closed and not auth error
        const shouldReconnect = !this.intentionalDisconnect &&
          event.code !== WS_CLOSE_CODES.NORMAL_CLOSURE &&
          event.code !== WS_CLOSE_CODES.AUTH_REQUIRED &&
          event.code !== WS_CLOSE_CODES.INVALID_TOKEN;
        
        if (shouldReconnect) {
          this.attemptReconnect(token);
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.emit('error', error);
      };
    } catch (err) {
      console.error('Failed to create WebSocket connection:', err);
    }
  }

  /**
   * Handle incoming WebSocket message
   * @param {Object} data - Parsed message data
   */
  handleMessage(data) {
    const { type, ...payload } = data;
    
    if (type === 'pong') {
      return; // Ignore pong responses
    }

    if (type === 'connected') {
      wsDebug('WebSocket connection acknowledged');
      return;
    }

    // Emit event to listeners
    this.emit(type, payload.data || payload);
  }

  /**
   * Attempt to reconnect to WebSocket server
   * @param {string} token - JWT authentication token
   */
  attemptReconnect(token) {
    if (this.intentionalDisconnect || this.reconnectAttempts >= this.maxReconnectAttempts) {
      wsDebug('Max reconnection attempts reached or intentional disconnect');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * this.reconnectAttempts;
    
    wsDebug(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);
    
    setTimeout(() => {
      if (!this.isConnected && !this.intentionalDisconnect) {
        this.connect(token);
      }
    }, delay);
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    this.intentionalDisconnect = true;
    if (this.ws) {
      this.ws.close(WS_CLOSE_CODES.NORMAL_CLOSURE, 'Client disconnecting');
      this.ws = null;
    }
    this.isConnected = false;
  }

  /**
   * Subscribe to a WebSocket event type
   * @param {string} eventType - Event type to listen for
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  on(eventType, callback) {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType).add(callback);

    // Return unsubscribe function
    return () => {
      const listeners = this.listeners.get(eventType);
      if (listeners) {
        listeners.delete(callback);
      }
    };
  }

  /**
   * Emit event to all listeners
   * @param {string} eventType - Event type
   * @param {Object} data - Event data
   */
  emit(eventType, data) {
    const listeners = this.listeners.get(eventType);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data);
        } catch (err) {
          console.error('Error in event listener:', err);
        }
      });
    }
  }

  /**
   * Send a message to the server
   * @param {Object} data - Data to send
   */
  send(data) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    }
  }

  /**
   * Check if connected
   * @returns {boolean}
   */
  getIsConnected() {
    return this.isConnected;
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();
